import {
  BodyMetricType,
  CommissionCategory,
  CommissionsVisibility,
  ConsentPolicyType,
  CurrencyType,
  DaysOfWeek,
  EventDurationMetric,
  EventType,
  GiftVoucherStatus,
  GiftVoucherTransactionType,
  NapVatStatus,
  PaymentTypes,
  PlanConsumerType,
  PlanDurationMetric,
  RecurringPeriodType,
  StatisticDetailedItemType,
  StatisticsType,
  StudioSettingsSection,
  StudioType,
  TimeStep,
  UnitOfMeasure,
  UserEventStatus,
  UserRole,
  WorkoutAchievementType,
} from "./enums";

export const CommissionCategoryLabels: Record<CommissionCategory, string> = {
  [CommissionCategory.Group]: "Групов",
  [CommissionCategory.Individual]: "Индивидуален",
  [CommissionCategory.Service]: "Услуга",
  [CommissionCategory.Multisport]: "MultiSport",
  [CommissionCategory.CoolFit]: "CoolFit",
  [CommissionCategory.Plan]: "План",
};

export const UnitOfMeasureLabels: Record<UnitOfMeasure, string> = {
  [UnitOfMeasure.Piece]: "Брой",
  [UnitOfMeasure.Gram]: "Грамаж",
};

/** Short form shown on list rows / quantity badges. */
export const UnitOfMeasureShortLabels: Record<UnitOfMeasure, string> = {
  [UnitOfMeasure.Piece]: "бр.",
  [UnitOfMeasure.Gram]: "гр.",
};

export const CurrencyTypeSymbols: Record<CurrencyType, string> = {
  [CurrencyType.BGN]: "лв.",
  [CurrencyType.EUR]: "€",
};

export const PlanDurationMetricLabels: Record<PlanDurationMetric, string> = {
  [PlanDurationMetric.Days]: "дни",
  [PlanDurationMetric.Months]: "месеца",
  [PlanDurationMetric.Years]: "години",
};

export const EventDurationMetricLabels: Record<EventDurationMetric, string> = {
  [EventDurationMetric.FifteenMin]: "15 min",
  [EventDurationMetric.ThirtyMin]: "30 min",
  [EventDurationMetric.FortyFiveMin]: "45 min",
  [EventDurationMetric.FiftyMin]: "50 min",
  [EventDurationMetric.FiftyFiveMin]: "55 min",
  [EventDurationMetric.OneHour]: "1 hr",
  [EventDurationMetric.OneHourFifteenMin]: "1 hr 15 min",
  [EventDurationMetric.OneHourThirtyMin]: "1 hr 30 min",
  [EventDurationMetric.OneHourFortyFiveMin]: "1 hr 45 min",
  [EventDurationMetric.TwoHours]: "2 hrs",
};

export const DaysOfWeekLabels: Record<DaysOfWeek, string> = {
  [DaysOfWeek.Monday]: "Понеделник",
  [DaysOfWeek.Tuesday]: "Вторник",
  [DaysOfWeek.Wednesday]: "Сряда",
  [DaysOfWeek.Thursday]: "Четвъртък",
  [DaysOfWeek.Friday]: "Петък",
  [DaysOfWeek.Saturday]: "Събота",
  [DaysOfWeek.Sunday]: "Неделя",
};

export const RecurringPeriodTypeLabels: Record<RecurringPeriodType, string> = {
  [RecurringPeriodType.Weeks]: "Седмици",
  [RecurringPeriodType.Months]: "Месеца",
};

export const StatisticsTypeLabels: Record<StatisticsType, string> = {
  [StatisticsType.Money]: "Приходи",
  [StatisticsType.Plans]: "Нови планове",
  [StatisticsType.Users]: "Нови потребители",
  [StatisticsType.Event]: "Присъствие на класове",
  [StatisticsType.Resources]: "Ресурси",
  [StatisticsType.NapAudit]: "НАП одиторски файл",
};

export const NapVatStatusLabels: Record<NapVatStatus, string> = {
  [NapVatStatus.Registered]: "Регистриран по ДДС (20%)",
  [NapVatStatus.Article97a]: "Регистриран по чл. 97а",
  [NapVatStatus.NotRegistered]: "Нерегистриран по ДДС",
};

export const PaymentTypesLabels: Record<PaymentTypes, string> = {
  [PaymentTypes.Cash]: "В брой",
  [PaymentTypes.POSCash]: "POS в брой",
  [PaymentTypes.Card]: "С карта",
  [PaymentTypes.Multisport]: "MultiSport",
  [PaymentTypes.Plan]: "Абонаментна карта",
  [PaymentTypes.Revolut]: "Revolut",
  [PaymentTypes.Bank]: "Банков път",
  [PaymentTypes.CoolFit]: "CoolFit",
  [PaymentTypes.MyPos]: "myPOS",
  [PaymentTypes.Stripe]: "Stripe",
  [PaymentTypes.Deposit]: "Депозит",
  [PaymentTypes.GiftVoucher]: "Подаръчен ваучер",
};

export const GiftVoucherStatusLabels: Record<GiftVoucherStatus, string> = {
  [GiftVoucherStatus.New]: "Нов",
  [GiftVoucherStatus.Sold]: "Продаден",
  [GiftVoucherStatus.Redeemed]: "Използван",
  [GiftVoucherStatus.Expired]: "Изтекъл",
};

export const GiftVoucherTransactionTypeLabels: Record<
  GiftVoucherTransactionType,
  string
> = {
  [GiftVoucherTransactionType.Issue]: "Продажба",
  [GiftVoucherTransactionType.Spend]: "Усвояване",
  [GiftVoucherTransactionType.Refund]: "Върната сума",
  [GiftVoucherTransactionType.Expire]: "Изтекъл срок",
};

export const PlanConsumerTypesLabels: Record<PlanConsumerType, string> = {
  [PlanConsumerType.User]: "Потребители",
  [PlanConsumerType.Family]: "Семейства",
};

export const StatisticDetailItemType: Record<
  StatisticDetailedItemType,
  string
> = {
  [StatisticDetailedItemType.Reservation]: "Посещение",
  [StatisticDetailedItemType.FamilyPlan]: "Семеен план",
  [StatisticDetailedItemType.UserPlan]: "Потребителски план",
  [StatisticDetailedItemType.Event]: "Клас",
  [StatisticDetailedItemType.InstructorVisits]: "Потребител",
  [StatisticDetailedItemType.InstructorPlans]: "Инструктор",
  [StatisticDetailedItemType.PlanCount]: "Продаден план",
  [StatisticDetailedItemType.ServiceReservation]: "Резервация за услуга",
  [StatisticDetailedItemType.EmailLog]: "Имейл",
  [StatisticDetailedItemType.SmsLog]: "SMS",
  [StatisticDetailedItemType.Order]: "Поръчка",
  [StatisticDetailedItemType.GymVisit]: "Посещение на фитнес",
  [StatisticDetailedItemType.DepositTransaction]: "Депозитна транзакция",
  [StatisticDetailedItemType.DepositDebtor]: "Длъжник",
  [StatisticDetailedItemType.DepositLiability]: "Депозитно задължение",
};

export const UserEventStatusLabels: Record<UserEventStatus, string> = {
  [UserEventStatus.Arrived]: "Пристигнал",
  [UserEventStatus.Confirmed]: "Потвърдена",
  [UserEventStatus.Missed]: "Изпуснат",
  [UserEventStatus.OnlyPaid]: "Платен без присъствие",
  [UserEventStatus.Pending]: "Очаква потвърждение",
  [UserEventStatus.WaitingList]: "В изчакващи",
  [UserEventStatus.PenaltyCancelled]: "Отказана",
};

export const EventTypeLabels: Record<EventType, string> = {
  [EventType.Group]: "Групов клас",
  [EventType.Individual]: "Индивидуален клас",
  [EventType.Service]: "Услуга",
  [EventType.BlockedTime]: "Блокирано време",
};

export const EventTypeShortLabels: Record<EventType, string> = {
  [EventType.Group]: "Групов",
  [EventType.Individual]: "Индивидуален",
  [EventType.Service]: "Услуга",
  [EventType.BlockedTime]: "Блокирано време",
};

export const UserRoleLabels: Record<UserRole, string> = {
  // Retired: merged into Employee_Instructor, which is now shown as Служител.
  // Only a few deleted users still hold it, and it passes every manager-level
  // check, so it keeps a label of its own.
  [UserRole.Employee]: "Служител (стара роля)",
  [UserRole.Employee_Instructor]: "Служител",
  [UserRole.Employee_Instructor_Group_Events]: "Служител - Групови тренировки",
  [UserRole.Employee_Manager]: "Мениджър",
  [UserRole.GlobalAdmin]: "Глобален админ",
  [UserRole.Tenant]: "Собственик",
  [UserRole.User]: "Потребител",
};

export const ConsentPolicyTypeLabels: Record<ConsentPolicyType, string> = {
  [ConsentPolicyType.PlannyPrivacyPolicy]: "Политика за поверителност",
  [ConsentPolicyType.AutomatedEmails]: "Автоматизирани имейли",
  [ConsentPolicyType.MarketingEmail]: "Маркетинг имейли",
  [ConsentPolicyType.TenantTermsOfUse]: "Общи условия за ползване",
  [ConsentPolicyType.PlannyTermsOfUse]: "Общи условия на платформата Planny",
};

export const StudioTypeLabels: Record<StudioType, string> = {
  [StudioType.Group]: "Групови тренировки",
  [StudioType.Service]: "Индивидуални услуги",
  [StudioType.Gym]: "Фитнес",
  [StudioType.Store]: "Магазин",
};

export const CommissionsVisibilityLabels: Record<CommissionsVisibility, string> =
  {
    [CommissionsVisibility.Both]: "И в двата репорта",
    [CommissionsVisibility.MoneyOnly]: "Само в репорт Приходи",
    [CommissionsVisibility.EventsOnly]: "Само в репорт Посещения",
    [CommissionsVisibility.None]: "Никъде (обектът не използва комисионни)",
  };

export const TimeStepLabels: Record<TimeStep, string> = {
  [TimeStep.FifteenMins]: "На всеки 15 минути",
  [TimeStep.ThirtyMins]: "На всеки 30 минути",
  [TimeStep.OneHour]: "На всеки 1 час",
};

export const WorkoutAchievementTypeLabels: Record<WorkoutAchievementType, string> = {
  [WorkoutAchievementType.MaxWeight]: "Най-високо тегло",
  [WorkoutAchievementType.MaxSetVolume]: "Най-голям обем на серия",
};

/** For the "Постижения" summary cards and filter chips, which must fit one line on a phone. */
export const WorkoutAchievementTypeShortLabels: Record<WorkoutAchievementType, string> = {
  [WorkoutAchievementType.MaxWeight]: "Тегло",
  [WorkoutAchievementType.MaxSetVolume]: "Обем",
};

export const BodyMetricTypeLabels: Record<BodyMetricType, string> = {
  [BodyMetricType.Weight]: "Тегло",
  [BodyMetricType.Height]: "Ръст",
  [BodyMetricType.BodyFat]: "Телесни мазнини",
  [BodyMetricType.MuscleMass]: "Мускулна маса",
  [BodyMetricType.BodyWater]: "Вода в тялото",
  [BodyMetricType.VisceralFat]: "Висцерални мазнини",
  [BodyMetricType.BoneMass]: "Костна маса",
  [BodyMetricType.RestingHeartRate]: "Пулс в покой",
  [BodyMetricType.Waist]: "Талия",
  [BodyMetricType.Chest]: "Гръдна обиколка",
  [BodyMetricType.Hips]: "Ханш",
  [BodyMetricType.Biceps]: "Бицепс",
  [BodyMetricType.Thigh]: "Бедро",
  [BodyMetricType.Calf]: "Прасец",
  [BodyMetricType.Neck]: "Врат",
  [BodyMetricType.MetabolicAge]: "Метаболитна възраст",
  [BodyMetricType.Vo2Max]: "VO2max",
};

/** Completes "Промени …" in the owner's "Достъп от Planny" log. */
export const StudioSettingsSectionLabels: Record<StudioSettingsSection, string> = {
  [StudioSettingsSection.General]: "основните настройки",
  [StudioSettingsSection.WorkingHours]: "работното време",
  [StudioSettingsSection.Logo]: "логото",
  [StudioSettingsSection.QrCodes]: "QR кодовете",
  [StudioSettingsSection.LinkPage]: "линк страницата",
};
